import React, { Component, Fragment } from "react";
import {
  Row,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import {
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import IntlMessages from "../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";

// END_POINT imported from defaultValues

// Provider icons mapping
const PROVIDER_ICONS = {
  ghn: "🚀",
  ghtk: "📦",
  viettelpost: "📮",
  jt: "🚚",
};

// Fallback providers when API is unavailable
const FALLBACK_PROVIDERS = [
  {
    name_key: "settings.shipping.provider.ghn.name",
    code: "ghn",
    description_key: "settings.shipping.provider.ghn.desc",
    isConnected: false,
  },
  {
    name_key: "settings.shipping.provider.ghtk.name",
    code: "ghtk",
    description_key: "settings.shipping.provider.ghtk.desc",
    isConnected: false,
  },
  {
    name_key: "settings.shipping.provider.viettelpost.name",
    code: "viettelpost",
    description_key: "settings.shipping.provider.viettelpost.desc",
    isConnected: false,
  },
  {
    name_key: "settings.shipping.provider.jt.name",
    code: "jt",
    description_key: "settings.shipping.provider.jt.desc",
    isConnected: false,
  },
];

class ShippingSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      providers: [],
      connectedProviders: [],
      businessType: "",
      selectedProvider: null,
      formData: {
        api_token: "",
        shop_id: "",
        client_id: "",
        secret_key: "",
        is_sandbox: false, // Default to production
        from_province_id: "",
        from_district_id: "",
        from_ward_code: "",
      },
      provinces: [],
      districts: [],
      wards: [],
      connecting: false,
      masked_api_token: "",
      masked_secret_key: "",
    };
  }

  componentDidMount() {
    this.loadProviders();
    this.loadConnectedProviders();
  }

  loadProviders = async () => {
    // Check permission: shipping requires basic
    if (!this.checkPermission("pro")) {
      // Use fallback providers without API call
      this.setState({
        providers: FALLBACK_PROVIDERS,
        businessType: localStorage.getItem("businessType") || "other",
        loading: false,
      });
      return;
    }
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/shipping/providers`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      // console.log("Shipping providers response:", response.data);

      if (
        response.data.success &&
        response.data.providers &&
        response.data.providers.length > 0
      ) {
        this.setState({
          providers: response.data.providers,
          businessType: response.data.businessType || "other",
          loading: false,
        });
      } else {
        // Use fallback providers
        console.log("Using fallback providers");
        this.setState({
          providers: FALLBACK_PROVIDERS,
          businessType: localStorage.getItem("businessType") || "other",
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error loading providers:", error);
      // Use fallback providers on error
      this.setState({
        providers: FALLBACK_PROVIDERS,
        businessType: localStorage.getItem("businessType") || "other",
        loading: false,
      });
    }
  };

  loadConnectedProviders = async () => {
    // Check permission: shipping requires basic
    if (!this.checkPermission("pro")) {
      return;
    }
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/shipping/connected`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (response.data.success) {
        this.setState({ connectedProviders: response.data.data });
      }
    } catch (error) {
      console.error("Error loading connected providers:", error);
    }
  };

  checkPermission = (requiredPackage) => {
    // Check permission from props (passed from Setting.js)
    const { license } = this.props;
    if (!license) return false;

    const ranks = {
      free: 0,
      basic: 1,
      pro: 2,
      pro_auto: 3,
      enterprise: 4,
    };

    const currentPackage = (license.purchased_package || "free").toLowerCase();
    const currentRank = ranks[currentPackage] || 0;
    const requiredRank = ranks[requiredPackage] || 0;

    // Check expiry only for paid packages
    if (currentRank > 0 && license.expired_date) {
      const today = new Date().toISOString().split("T")[0];
      if (license.expired_date < today) {
        return 0 >= requiredRank;
      }
    }

    return currentRank >= requiredRank;
  };

  handleSelectProvider = (provider) => {
    this.setState({
      selectedProvider: provider,
      formData: {
        api_token: "",
        shop_id: "",
        client_id: "",
        secret_key: "",
        is_sandbox: false,
        from_province_id: "",
        from_district_id: "",
        from_ward_code: "",
      },
      masked_api_token: "",
      masked_secret_key: "",
    });
    this.fetchProvinces(provider.code);
  };

  handleEditProvider = (provider) => {
    this.setState(
      {
        selectedProvider: { code: provider.provider, name: provider.name },
        formData: {
          api_token: "",
          shop_id: provider.shop_id || "",
          client_id: provider.client_id || "",
          secret_key: "",
          is_sandbox: provider.is_sandbox,
          from_province_id: provider.from_province_id || "",
          from_district_id: provider.from_district_id || "",
          from_ward_code: provider.from_ward_code || "",
        },
        masked_api_token: provider.api_token || "",
        masked_secret_key: provider.secret_key || "",
      },
      () => {
        if (provider.from_province_id)
          this.fetchDistricts(provider.provider, provider.from_province_id);
        if (provider.from_district_id)
          this.fetchWards(provider.provider, provider.from_district_id);
      },
    );
    this.fetchProvinces(provider.provider);
  };

  fetchProvinces = async (provider) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/shipping/provinces/${provider}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      if (res.data.success) {
        this.setState({ provinces: res.data.data });
      }
    } catch (err) {
      console.error("fetchProvinces error", err);
    }
  };

  fetchDistricts = async (provider, provinceId) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/shipping/districts/${provider}/${provinceId}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      if (res.data.success) {
        this.setState({ districts: res.data.data });
      }
    } catch (err) {
      console.error("fetchDistricts error", err);
    }
  };

  fetchWards = async (provider, districtId) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/shipping/wards/${provider}/${districtId}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      if (res.data.success) {
        this.setState({ wards: res.data.data });
      }
    } catch (err) {
      console.error("fetchWards error", err);
    }
  };

  handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      formData: {
        ...prev.formData,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  handleConnect = async () => {
    const { selectedProvider, formData } = this.state;

    if (!formData.api_token && selectedProvider.code !== "jt") {
      NotificatioErrController("Vui lòng nhập API Token");
      return;
    }

    this.setState({ connecting: true });

    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/shipping/connect`,
        {
          provider: selectedProvider.code,
          ...formData,
        },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      if (response.data.success) {
        NotificatioSucController(response.data.message);
        this.loadProviders();
        this.loadConnectedProviders();
        this.setState({ selectedProvider: null });
      } else {
        NotificatioErrController(response.data.message);
      }
    } catch (error) {
      NotificatioErrController("Lỗi kết nối đơn vị vận chuyển");
    }

    this.setState({ connecting: false });
  };

  handleDisconnect = async (providerCode) => {
    if (
      !window.confirm("Bạn có chắc muốn ngắt kết nối đơn vị vận chuyển này?")
    ) {
      return;
    }

    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/shipping/disconnect`,
        { provider: providerCode },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      if (response.data.success) {
        NotificatioSucController("Đã ngắt kết nối");
        this.loadProviders();
        this.loadConnectedProviders();
      } else {
        NotificatioErrController(response.data.message);
      }
    } catch (error) {
      NotificatioErrController("Lỗi ngắt kết nối");
    }
  };

  handleSetDefault = async (providerCode) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/shipping/set-default`,
        { provider: providerCode },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      if (response.data.success) {
        NotificatioSucController("Đã đặt mặc định");
        this.loadConnectedProviders();
      }
    } catch (error) {
      NotificatioErrController("Lỗi đặt mặc định");
    }
  };

  renderProviderModal = () => {
    const { selectedProvider, formData, connecting } = this.state;

    return (
      <Modal
        isOpen={!!selectedProvider}
        toggle={() => this.setState({ selectedProvider: null })}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => this.setState({ selectedProvider: null })}>
          {selectedProvider && (
            <>
              {PROVIDER_ICONS[selectedProvider.code]}{" "}
              <IntlMessages
                id="settings.shipping.modal.connect-title"
                values={{ name: selectedProvider.name }}
              />
            </>
          )}
        </ModalHeader>
        <ModalBody>
          {selectedProvider && (
            <div className="shipping-provider-form">
              <p className="text-muted">{selectedProvider.description}</p>

              <div className="mb-3">
                <Label>
                  <IntlMessages id="settings.shipping.modal.token" /> *
                </Label>
                <Input
                  type="text"
                  name="api_token"
                  value={formData.api_token}
                  onChange={this.handleInputChange}
                  placeholder={
                    this.state.masked_api_token ||
                    this.props.intl.formatMessage({
                      id: "settings.shipping.modal.token-placeholder",
                    })
                  }
                />
                {this.state.masked_api_token && (
                  <small className="text-success d-block">
                    <IntlMessages
                      id="settings.shipping.modal.current-token"
                      values={{ token: this.state.masked_api_token }}
                    />
                  </small>
                )}
                <small className="text-muted">
                  {selectedProvider.code === "ghn" && (
                    <IntlMessages id="settings.shipping.modal.token-guide.ghn" />
                  )}
                  {selectedProvider.code === "ghtk" && (
                    <IntlMessages id="settings.shipping.modal.token-guide.ghtk" />
                  )}
                  {selectedProvider.code === "viettelpost" && (
                    <IntlMessages id="settings.shipping.modal.token-guide.viettel" />
                  )}
                  {selectedProvider.code === "jt" && (
                    <IntlMessages id="settings.shipping.modal.token-guide.jt" />
                  )}
                </small>
              </div>

              {(selectedProvider.code === "ghn" ||
                selectedProvider.code === "ghtk") && (
                <div className="mb-3">
                  <Label>
                    <IntlMessages id="settings.shipping.modal.shop-id" />
                  </Label>
                  <Input
                    type="text"
                    name="shop_id"
                    value={formData.shop_id}
                    onChange={this.handleInputChange}
                    placeholder={this.props.intl.formatMessage({
                      id: "settings.shipping.modal.shop-id-placeholder",
                    })}
                  />
                </div>
              )}

              {selectedProvider.code === "jt" && (
                <>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="settings.shipping.modal.client-id" /> *
                    </Label>
                    <Input
                      type="text"
                      name="client_id"
                      value={formData.client_id}
                      onChange={this.handleInputChange}
                      placeholder={this.props.intl.formatMessage({
                        id: "settings.shipping.modal.client-id-placeholder",
                      })}
                    />
                  </div>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="settings.shipping.modal.secret-key" /> *
                    </Label>
                    <Input
                      type="password"
                      name="secret_key"
                      value={formData.secret_key}
                      onChange={this.handleInputChange}
                      placeholder={this.props.intl.formatMessage({
                        id: "settings.shipping.modal.secret-key-placeholder",
                      })}
                    />
                  </div>
                </>
              )}

              <div className="mb-3">
                <Label className="d-flex align-items-center">
                  <Input
                    type="checkbox"
                    name="is_sandbox"
                    checked={formData.is_sandbox}
                    onChange={this.handleInputChange}
                    className="me-2"
                  />
                  <IntlMessages id="settings.shipping.modal.sandbox-label" />
                </Label>
              </div>

              <hr />
              <h6>
                <IntlMessages id="settings.shipping.modal.origin-title" />
              </h6>
              <p className="text-muted small">
                <IntlMessages id="settings.shipping.modal.origin-desc" />
              </p>

              <Row>
                <Colxx md="4">
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="settings.shipping.modal.province" />
                    </Label>
                    <select
                      className="form-control"
                      name="from_province_id"
                      value={formData.from_province_id}
                      onChange={(e) => {
                        this.handleInputChange(e);
                        this.fetchDistricts(
                          selectedProvider.code,
                          e.target.value,
                        );
                      }}
                    >
                      <option value="">
                        <IntlMessages id="settings.shipping.modal.select-province" />
                      </option>
                      {this.state.provinces.map((p) => (
                        <option
                          key={p.id || p.ProvinceID}
                          value={p.id || p.ProvinceID}
                        >
                          {p.name || p.ProvinceName}
                        </option>
                      ))}
                    </select>
                  </div>
                </Colxx>
                <Colxx md="4">
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="settings.shipping.modal.district" />
                    </Label>
                    <select
                      className="form-control"
                      name="from_district_id"
                      value={formData.from_district_id}
                      onChange={(e) => {
                        this.handleInputChange(e);
                        this.fetchWards(selectedProvider.code, e.target.value);
                      }}
                      disabled={!formData.from_province_id}
                    >
                      <option value="">
                        <IntlMessages id="settings.shipping.modal.select-district" />
                      </option>
                      {this.state.districts.map((d) => (
                        <option
                          key={d.id || d.DistrictID}
                          value={d.id || d.DistrictID}
                        >
                          {d.name || d.DistrictName}
                        </option>
                      ))}
                    </select>
                  </div>
                </Colxx>
                <Colxx md="4">
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="settings.shipping.modal.ward" />
                    </Label>
                    <select
                      className="form-control"
                      name="from_ward_code"
                      value={formData.from_ward_code}
                      onChange={this.handleInputChange}
                      disabled={!formData.from_district_id}
                    >
                      <option value="">
                        <IntlMessages id="settings.shipping.modal.select-ward" />
                      </option>
                      {this.state.wards.map((w) => (
                        <option
                          key={w.id || w.WardCode}
                          value={w.id || w.WardCode}
                        >
                          {w.name || w.WardName}
                        </option>
                      ))}
                    </select>
                  </div>
                </Colxx>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => this.setState({ selectedProvider: null })}
          >
            <IntlMessages id="settings.shipping.modal.cancel" />
          </Button>
          <Button
            color="primary"
            onClick={this.handleConnect}
            disabled={connecting}
          >
            {connecting ? (
              <IntlMessages id="settings.shipping.modal.connecting" />
            ) : (
              <IntlMessages id="settings.shipping.modal.connect" />
            )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  render() {
    const {
      loading,
      providers,
      connectedProviders,
      businessType,
      selectedProvider,
    } = this.state;

    return (
      <Fragment>
        <Row>
          <Colxx xxs="12">
            <h1>
              <IntlMessages id="settings.shipping.title" />
            </h1>
            <p className="text-muted">
              <IntlMessages id="settings.shipping.subtitle" />
            </p>
            <Separator className="mb-4" />
          </Colxx>
        </Row>

        <Row className="mb-5">
          <Colxx xxs="12" lg="8">
            {/* Permission Check */}
            {!this.checkPermission("pro") && (
              <Alert color="warning" className="mb-4">
                <IntlMessages id="settings.shipping.upgrade-warn" />{" "}
                <a
                  href="/app/applications/upgrade"
                  className="alert-link font-weight-bold"
                >
                  <IntlMessages id="settings.shipping.upgrade-btn" />
                </a>
              </Alert>
            )}

            <div
              style={
                !this.checkPermission("pro")
                  ? { pointerEvents: "none", opacity: 0.6 }
                  : {}
              }
            >
              {/* Business Type Info */}
              <Alert color="info" className="mb-4">
                <strong>
                  <IntlMessages id="settings.shipping.business-type-label" />
                </strong>{" "}
                <Badge color="primary">
                  {businessType === "cafe" && (
                    <IntlMessages id="settings.business.cafe" />
                  )}
                  {businessType === "restaurant" && (
                    <IntlMessages id="settings.business.restaurant" />
                  )}
                  {businessType === "retail" && (
                    <IntlMessages id="settings.business.retail" />
                  )}
                  {businessType === "other" && (
                    <IntlMessages id="settings.business.other" />
                  )}
                </Badge>
                <br />
                <small>
                  <IntlMessages id="settings.shipping.business-fit" />
                </small>
              </Alert>

              {/* Connected Providers */}
              {connectedProviders.length > 0 && (
                <Card className="mb-4">
                  <CardBody>
                    <h5>
                      ✅ <IntlMessages id="settings.shipping.connected-title" />
                    </h5>
                    {connectedProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className="d-flex justify-content-between align-items-center p-3 border mb-2"
                        style={{
                          // backgroundColor: "#f8f9fa",
                          borderRadius: "0.75rem",
                        }}
                      >
                        <div>
                          <strong>
                            {PROVIDER_ICONS[provider.provider]} {provider.name}
                          </strong>
                          {provider.is_sandbox && (
                            <Badge color="warning" className="mx-2">
                              Sandbox
                            </Badge>
                          )}
                          <div className="small text-muted">
                            Token: {provider.api_token || "***"}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            color="outline-info"
                            onClick={() => this.handleEditProvider(provider)}
                            className="mx-2"
                          >
                            <IntlMessages id="settings.shipping.edit-btn" />
                          </Button>
                          <Button
                            size="sm"
                            color="outline-danger"
                            onClick={() =>
                              this.handleDisconnect(provider.provider)
                            }
                          >
                            <IntlMessages id="settings.shipping.disconnect-btn" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              )}

              {/* Available Providers */}
              <Card className="mb-4">
                <CardBody>
                  <h5>
                    🚚 <IntlMessages id="settings.shipping.available-title" />
                  </h5>
                  {loading ? (
                    <div className="text-center p-4">Đang tải...</div>
                  ) : (
                    <Row>
                      {providers.map((provider) => (
                        <Colxx
                          xxs="12"
                          md="6"
                          key={provider.code}
                          className="mb-3"
                        >
                          <div
                            className="p-3 border h-100"
                            style={{
                              cursor: provider.isConnected
                                ? "default"
                                : "pointer",
                              backgroundColor: provider.isConnected
                                ? "#e8f5e9"
                                : "",
                              borderColor:
                                selectedProvider &&
                                selectedProvider.code === provider.code
                                  ? "#007bff"
                                  : "",
                              borderRadius: "0.75rem",
                            }}
                            onClick={() =>
                              !provider.isConnected &&
                              this.handleSelectProvider(provider)
                            }
                          >
                            <div className="d-flex align-items-center mb-2">
                              <span
                                style={{
                                  fontSize: "24px",
                                  marginRight: "10px",
                                }}
                              >
                                {PROVIDER_ICONS[provider.code]}
                              </span>
                              <div>
                                <strong>
                                  {provider.name_key ? (
                                    <IntlMessages id={provider.name_key} />
                                  ) : (
                                    provider.name
                                  )}
                                </strong>
                                {provider.isConnected && (
                                  <Badge color="success" className="mx-2">
                                    <IntlMessages id="settings.shipping.status-connected" />
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <p className="small text-muted mb-0">
                              {provider.description_key ? (
                                <IntlMessages id={provider.description_key} />
                              ) : (
                                provider.description
                              )}
                            </p>
                            {!provider.isConnected && (
                              <Button
                                size="sm"
                                color="outline-primary"
                                className="mt-2"
                              >
                                <IntlMessages id="settings.shipping.connect-btn" />
                              </Button>
                            )}
                          </div>
                        </Colxx>
                      ))}
                    </Row>
                  )}
                </CardBody>
              </Card>

              {/* Connection Form - Removed, now using Modal */}
            </div>
          </Colxx>

          {/* Help Section */}
          <Colxx xxs="12" lg="4">
            <Card>
              <CardBody>
                <h5>
                  📖 <IntlMessages id="settings.shipping.guide-title" />
                </h5>
                <hr />
                <h6>GHN (Giao Hàng Nhanh)</h6>
                <ol className="small">
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghn.step1" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghn.step2" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghn.step3" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghn.step4" />
                  </li>
                </ol>

                <h6>GHTK (Giao Hàng Tiết Kiệm)</h6>
                <ol className="small">
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghtk.step1" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghtk.step2" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.ghtk.step3" />
                  </li>
                </ol>

                <h6>Viettel Post</h6>
                <ol className="small">
                  <li>
                    <IntlMessages id="settings.shipping.guide.viettel.step1" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.viettel.step2" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.viettel.step3" />
                  </li>
                </ol>

                <h6>J&T Express</h6>
                <ol className="small">
                  <li>
                    <IntlMessages id="settings.shipping.guide.jt.step1" />
                  </li>
                  <li>
                    <IntlMessages id="settings.shipping.guide.jt.step2" />
                  </li>
                </ol>
              </CardBody>
            </Card>
          </Colxx>
        </Row>

        {/* Shipping Provider Connection Modal */}
        {this.renderProviderModal()}
      </Fragment>
    );
  }
}

export default injectIntl(ShippingSettings);
